import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Modal,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { LazyEmojiPicker } from './LazyEmojiPicker';
import { useDesktopPicker } from './useDesktopPicker';
import type { ChatMessage } from './messageUtils';
import { useT } from '../../i18n';

// "＋" in the quick row (M69, D264): the full picker in reaction mode. One
// tap reacts and closes. A bottom sheet on phones, a dialog on desktop.
export function ReactionPicker({
  message,
  onReact,
  onClose,
}: {
  message: ChatMessage | null;
  onReact: (message: ChatMessage, emoji: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const desktop = useDesktopPicker();
  // Once per opening: a quick second tap before the sheet closes is ignored.
  const reacted = useRef<string | null>(null);
  useEffect(() => {
    if (message) reacted.current = null;
  }, [message]);
  if (!message) return null;
  const picker = (
    <LazyEmojiPicker
      mode="react"
      autoFocusSearch={desktop}
      onPick={(emoji) => {
        if (reacted.current === message.id) return;
        reacted.current = message.id;
        onClose();
        onReact(message, emoji);
      }}
      onClose={onClose}
    />
  );
  if (desktop) {
    return (
      <Modal isOpen onClose={onClose} isCentered size="sm">
        <ModalOverlay bg="rgba(31, 29, 27, 0.35)" />
        <ModalContent
          h="440px"
          maxH="80vh"
          bg="surface"
          borderRadius="card"
          overflow="hidden"
          aria-label={t('chat.emoji.reactTitle')}
        >
          {picker}
        </ModalContent>
      </Modal>
    );
  }
  return (
    // No autofocus: focusing the search box would raise the keyboard over
    // the emojis.
    <Drawer isOpen placement="bottom" onClose={onClose} autoFocus={false}>
      <DrawerOverlay bg="rgba(31, 29, 27, 0.55)" />
      <DrawerContent
        h="min(60vh, 460px)"
        borderTopRadius="28px"
        bg="surface"
        pb="env(safe-area-inset-bottom)"
        aria-label={t('chat.emoji.reactTitle')}
      >
        <Box
          w="40px"
          h="5px"
          borderRadius="3px"
          bg="gray.200"
          mx="auto"
          mt="10px"
          flexShrink={0}
          aria-hidden="true"
        />
        <Box flex="1" minH={0}>
          {picker}
        </Box>
      </DrawerContent>
    </Drawer>
  );
}
